import { ICard } from './card-types';
import { AutoTopUpSettingsDto } from './dto/table/auto-top-up-settings';
import { UserPublic } from './dto/user/user.dto';
import { OFCBoardDto } from './ofc-types';
import { AvailableActions } from './table-hand-types';

export interface PreAction {
  label?: string;
  amount?: number;
}

export interface PreActions {
  foldCheck?: PreAction;
  checkCall?: PreAction;
  betRaise?: PreAction;
  standPatOrDiscard?: PreAction;
}

/**
 * Unified seat DTO for both poker and OFC tables.
 *
 * Poker-specific fields (cardIndexes, holeCards, betAmount, etc.) are present for poker tables.
 * OFC-specific fields (sessionPoints, isFantasy, board, etc.) are present for OFC tables.
 * The `category` field on TableStateDto indicates which game type ('Chinese' for OFC).
 */
export interface SeatDto {
  seat: number;
  userId: string;
  alias: string;
  stack: number;
  totalStack?: number;
  active: boolean;
  playerIndex: number | null;
  isActingPlayer: boolean;
  isDealer: boolean;
  timer?: number;
  usingTimeBank?: boolean;
  usingDisconnectionTimebank?: boolean;
  timeRemaining?: number;
  disconnected?: boolean;
  leftTable?: boolean;
  newPlayer?: boolean;
  reserved?: boolean;
  reservedUntil?: number; // timestamp when reservation expires (from waitlist)
  joining?: boolean; // true when user clicked "Take Seat" but hasn't confirmed buy-in
  joiningUntil?: number; // timestamp when joining expires
  addingChips?: boolean;
  user?: Partial<{ [K in keyof UserPublic]: UserPublic[K] | null }> & {
    isStreamer?: boolean;
    streamName?: string | null;
    streamUrl?: string | null;
    streamingNow?: boolean;
    isAmbassador?: boolean;
  } & Record<string, unknown>;

  // Poker-specific fields (optional, only present for poker tables)
  cardIndexes?: number[];
  betAmount?: number;
  holeCards?: ICard[];
  cardsDiscarded?: number | null;
  skippedBB?: boolean;
  skippedSB?: boolean;
  status?: boolean;

  // OFC-specific fields (optional, only present for OFC tables)
  sessionPoints?: number;
  isFantasy?: boolean;
  fantasyStreak?: number;
  cardsInHandCount?: number;
  board?: OFCBoardDto | null;
  timebank?: number;
  disconnectionTimebank?: number;
  hasConfirmed?: boolean;
  discardCount?: number;
}

export interface PlayerSeatDto extends SeatDto, AutoTopUpSettingsDto {
  buyIn: number | null;
  waiting: boolean;
  timebank: number;
  disconnectionTimebank: number;
  preActionsAvailable?: PreActions | null;
  availableActions?: AvailableActions | null;
  leftTable: boolean;
  handsDealt: number;
  foldedCards: ICard[];
  foldedCardRevealIndexes: number[];
}

export interface SeatEquityDto {
  oddsPercent: number;
  /** For hi/lo split pot games (e.g., Omaha Eight or Better) */
  highPercent?: number;
  lowPercent?: number;
}

/** Equity data for a single board */
export interface BoardEquityDto {
  /** Board index (0 for first/shared, 1+ for runouts or double boards) */
  boardIndex: number;
  /** Equities per seat for this board */
  equities: Record<number, SeatEquityDto>;
}

export interface AllInEquityDto {
  /** Single board equity (backwards compatible) */
  equities: Record<number, SeatEquityDto>;
  /** Per-board equities for multi-board games (runouts, double board) */
  boardEquities?: BoardEquityDto[];
  /** Whether this is a hi/lo split pot game */
  isHiLo?: boolean;
  /** Number of boards (1 for single, 2+ for runouts/double board) */
  boardCount?: number;
}

export interface SeatReadyTableInfo {
  seat: number;
  url: string;
  stack: number;
  name: string;
  ante: number | null;
  smallBet: number | null;
  bigBet: number | null;
  smallBlind: number | null;
  bigBlind: number | null;
  currentVariant: string | null;
  attemptJoiningAt: Date;
  timeout: number;
}

export interface SeatReadyEventTableInfo {
  tableId: string;
  userId: string;
  playSound?: boolean;
  tableInfo: SeatReadyTableInfo;
}
